import { useAtomValue } from 'jotai';

import { useViewedEmployee } from './useViewedEmployee';

import type { User } from '@/types/directory/User';

import {
    useGetAllCompanyOrTeamUsers,
    useGetUserById,
    useGetUserPermissions,
} from '@/api/directory';
import { userInstanceAtom } from '@/atoms/userDetails';
import { getFullName, getInitials, getProfilePictureUrl } from '@/utils/user';

/** Someone whose portal the reader may open, as the picker lists them. */
export interface PortalPerson {
    id: string;
    name: string;
    initials: string;
    avatarUrl: string;
    /** The reader themselves, whose own portal is the one the picker opens on. */
    isReader: boolean;
}

export interface PortalAudience {
    /**
     * Whether the reader manages anyone, and is therefore offered the picker at all -- `undefined`
     * while their permissions are not known, so nothing flashes in and out of the tab row.
     */
    isOffered: boolean | undefined;
    /**
     * The people to list, `undefined` while they are on their way. The wait is that absence and
     * nothing else: an empty list here means the reader reaches no one, which the picker says.
     */
    people: PortalPerson[] | undefined;
    /** The list could not be read; the picker says so rather than showing it empty. */
    isError: boolean;
    /**
     * Whose portal is on screen, `undefined` on the reader's own. The address alone cannot answer
     * that: one naming the reader themselves is their own portal, and it is a link a manager
     * shares readily enough -- "regarde ton portail" -- that the page has to recognise it, or it
     * speaks to someone about themselves in the third person. `undefined` until the reader is
     * known, too, because that is when the question has no answer yet.
     */
    viewedEmployeeId: string | undefined;
    /** The listed person the picker shows as chosen; empty while the reader is on their own portal. */
    selectedId: string;
    /** Opens someone's portal -- the reader's own when they pick themselves. */
    openPortalOf: (employeeId: string) => void;
}

const toPortalPerson =
    (readerId: string) =>
    (user: User): PortalPerson => ({
        id: user.id,
        name: getFullName(user),
        initials: getInitials(user),
        avatarUrl: getProfilePictureUrl(user.profilePictureUrl),
        isReader: user.id === readerId,
    });

/**
 * Whose portals the reader may open: their own, and everyone their scope reaches -- a manager's
 * whole reporting line, or the entire company for someone who manages all departments or
 * administers the company.
 * That scope is the server's own: it answers 404 for anyone outside it whatever the address says,
 * so what is listed here decides what is offered, never what is allowed.
 *
 * A reader who manages no one is offered nothing, and the tab row keeps the shape it has today.
 */
export const usePortalAudience = (): PortalAudience => {
    const { data: permissions } = useGetUserPermissions();
    const { data: reader, isError: isReaderUnknown } = useGetUserById({ userId: 'me' });
    const { viewedEmployeeId: addressedEmployeeId, viewPortalOf } = useViewedEmployee();
    // Whether the reader administers the company is a fact about the session, not a permission.
    const { isAdminUser } = useAtomValue(userInstanceAtom);

    const readsWholeCompany = isAdminUser || permissions?.canManageAllDepartmentsPerformance;
    const managesTeam = permissions?.canManageTeamPerformance;
    const isOffered = permissions === undefined ? undefined : !!(readsWholeCompany || managesTeam);

    const { data: reachedUsers, isError: areUsersUnreadable } = useGetAllCompanyOrTeamUsers({
        enabled: isOffered === true,
        canManageAllDepartmentsPerformance: readsWholeCompany,
        canManageTeamPerformance: managesTeam,
        // The reader's own portal is one of the choices, so they can come back to it from here.
        includeSelf: true,
    });

    // The reader is put in the list here rather than taken from it. The scoped endpoint does carry
    // them, but only because it is asked to: leaving the way back to one's own portal to an option
    // of a request would mean losing it the day that request stops carrying it, inside a list of
    // everyone in the company where nobody would notice it had gone.
    const people =
        reachedUsers && reader
            ? [reader, ...reachedUsers.filter((user) => user.id !== reader.id)].map(
                  toPortalPerson(reader.id),
              )
            : undefined;

    // One question, asked of two different people: the picker asks it of whoever is being picked,
    // the page of whoever the address names. Answered here once so the two cannot drift apart.
    const isReader = (employeeId: string | undefined) => employeeId === reader?.id;
    const viewedEmployeeId =
        reader && !isReader(addressedEmployeeId) ? addressedEmployeeId : undefined;

    return {
        isOffered,
        people,
        isError: areUsersUnreadable || isReaderUnknown,
        viewedEmployeeId,
        selectedId: viewedEmployeeId ?? '',
        openPortalOf: (employeeId) => viewPortalOf(isReader(employeeId) ? undefined : employeeId),
    };
};
